import React, { useContext, useEffect, useReducer, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'

// Componentes do Material-UI
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import ListSubheader from '@material-ui/core/ListSubheader'
import Divider from '@material-ui/core/Divider'
import { Badge, Collapse, Tooltip, Zoom } from '@material-ui/core'

// Contextos
import { WhatsAppsContext } from '../context/WhatsApp/WhatsAppsContext'
import { AuthContext } from '../context/Auth/AuthContext'
import { SocketContext } from '../context/Socket/SocketContext'

// Utilitários
import { i18n } from '../translate/i18n'
import { isArray } from 'lodash'
import api from '../services/api'
import toastError from '../errors/toastError'
import { makeStyles, withStyles } from '@material-ui/core/styles'

// Hooks personalizados
import usePlans from '../hooks/usePlans'
import useVersion from '../hooks/useVersion'
import usePermission from '../hooks/usePermission'

// Ícones de traço fino (Feather Icons)
import {
  FiMessageSquare,
  FiZap,
  FiUsers,
  FiUser,
  FiLayers,
  FiSettings,
  FiRefreshCw,
  FiHelpCircle,
  FiCode,
  FiCalendar,
  FiClipboard,
  FiTag,
  FiCheckSquare,
  FiBell,
  FiDollarSign,
  FiDatabase,
  FiLogOut,
  FiHome,
  FiBarChart2,
  FiMail,
  FiList,
  FiCpu,
  FiLink,
  FiFolder,
  FiGrid,
  FiKey,
  FiBriefcase,
  FiFileText,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi'

import { getCrmMenuItems, isCrmMenuItemActive, isCrmPath } from './crmMenuItems'
import { getAgendaMenuItems, isAgendaMenuItemActive, isAgendaPath } from './agendaMenuItems'

const useStyles = makeStyles(theme => ({
  ListSubheader: {
    height: 26,
    marginTop: '-15px',
    marginBottom: '-10px',
  },
  logoutButton: {
    borderRadius: 10,
    marginTop: 10,
    backgroundColor: theme.palette.sair.main,
    color: theme.palette.text.sair,
    '&:hover': {
      backgroundColor: theme.palette.sair.dark,
    },
  },
  listItem: {
    borderRadius: 8,
    margin: '4px 8px',

    justifyContent: collapsed => (collapsed ? 'center' : 'flex-start'),
    minHeight: 48,
    padding: collapsed => (collapsed ? '8px 12px' : '8px 16px'),
  },
  listItemIcon: {
    minWidth: collapsed => (collapsed ? 0 : 40),
    marginRight: collapsed => (collapsed ? 0 : theme.spacing(2)),
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  listItemText: {
    '& span': {
      fontSize: '0.9rem',
      fontWeight: 500,
    },
    opacity: collapsed => (collapsed ? 0 : 1),
    transition: 'opacity 0.2s ease',
    display: collapsed => (collapsed ? 'none' : 'block'),
  },
  subheader: {
    position: 'relative',
    fontSize: '13px',
    textAlign: 'left',
    paddingLeft: 28,
    color: theme.palette.primary.main,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    opacity: collapsed => (collapsed ? 0 : 1),
    transition: 'opacity 0.2s ease',
    display: collapsed => (collapsed ? 'none' : 'block'),
  },
  versionBadge: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    fontSize: '10px',
    padding: '2px 8px',
    borderRadius: '12px',
    fontWeight: 'bold',
  },
  // O recuo fica na lista, e não no item, porque o padding do item é dinâmico e sobrescreveria um padding fixo.
  nestedList: {
    listStyle: 'none',
    margin: '0 0 4px 30px',
    padding: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  nestedItem: {
    minHeight: 38,
    '& $listItemText span': {
      fontSize: '0.85rem',
    },
  },
  groupChevron: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
  },
  connectionBadge: {
    '& .MuiBadge-badge': {
      right: -5,
      top: 8,
      padding: '0 4px',
      backgroundColor: theme.palette.error.main,
      fontWeight: 'bold',
      fontSize: '10px',
    },
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeItem: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    '& $listItemIcon': {
      color: theme.palette.primary.main,
    },
  },
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  menuWrapper: {
    width: collapsed => (collapsed ? 40 : 280),
    transition: 'width 0.3s ease',
    overflow: 'hidden',
  },
  versionContainer: {
    fontSize: '12px',
    padding: '10px',
    textAlign: 'right',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px',
    opacity: collapsed => (collapsed ? 0 : 1),
    transition: 'opacity 0.2s ease',
  },
}))

// Badge personalizado para notificações
const StyledBadge = withStyles(theme => ({
  badge: {
    right: -3,
    top: 8,
    padding: '0 4px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
}))(Badge)

// Componente Tooltip personalizado
const CustomTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    fontSize: '0.8rem',
    fontWeight: 500,
    borderRadius: 8,
  },
}))(Tooltip)

function ListItemLink(props) {
  const { icon, primary, to, className, isActive, collapsed } = props
  const classes = useStyles(collapsed)

  const renderLink = React.useMemo(() => {
    const linkComponent = React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />)
    linkComponent.displayName = 'ListItemLinkRouter'
    return linkComponent
  }, [to])

  const listItemContent = (
    <ListItem button dense component={renderLink} className={`${classes.listItem} ${className || ''} ${isActive ? classes.activeItem : ''}`}>
      <ListItemIcon className={classes.listItemIcon}>
        <div className={classes.iconWrapper}>{icon}</div>
      </ListItemIcon>
      <ListItemText primary={primary} className={classes.listItemText} />
    </ListItem>
  )

  if (collapsed) {
    return (
      <li>
        <CustomTooltip title={primary} placement='right' TransitionComponent={Zoom} arrow>
          {listItemContent}
        </CustomTooltip>
      </li>
    )
  }

  return <li>{listItemContent}</li>
}

const CRM_MENU_ICONS = {
  board: <FiBriefcase size={18} />,
  conversions: <FiBarChart2 size={18} />,
  reports: <FiFileText size={18} />,
  settings: <FiSettings size={18} />,
}

const AGENDA_MENU_ICONS = {
  calendar: <FiCalendar size={18} />,
  reports: <FiFileText size={18} />,
  settings: <FiSettings size={18} />,
}

function MenuGroup({ label, icon, items, icons, insideGroup, isItemActive, activePath, collapsed }) {
  const classes = useStyles(collapsed)
  const [open, setOpen] = useState(insideGroup)

  useEffect(() => {
    if (insideGroup) {
      setOpen(true)
    }
  }, [insideGroup])

  if (!items.length) {
    return null
  }

  if (collapsed || items.length === 1) {
    return items.map(item => <ListItemLink key={item.key} to={item.to} primary={i18n.t(item.fullLabelKey)} icon={icons[item.key]} isActive={isItemActive(item, activePath)} collapsed={collapsed} />)
  }

  const hasActiveItem = items.some(item => isItemActive(item, activePath))

  const handleToggle = event => {
    event.stopPropagation()
    setOpen(current => !current)
  }

  return (
    <>
      <li>
        <ListItem button dense onClick={handleToggle} aria-expanded={open} className={`${classes.listItem} ${!open && hasActiveItem ? classes.activeItem : ''}`}>
          <ListItemIcon className={classes.listItemIcon}>
            <div className={classes.iconWrapper}>{icon}</div>
          </ListItemIcon>
          <ListItemText primary={label} className={classes.listItemText} />
          <span className={classes.groupChevron}>{open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}</span>
        </ListItem>
      </li>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <ul className={classes.nestedList}>
          {items.map(item => (
            <ListItemLink
              key={item.key}
              to={item.to}
              primary={i18n.t(item.labelKey)}
              icon={icons[item.key]}
              className={classes.nestedItem}
              isActive={isItemActive(item, activePath)}
              collapsed={collapsed}
            />
          ))}
        </ul>
      </Collapse>
    </>
  )
}

const reducer = (state, action) => {
  if (action.type === 'LOAD_CHATS') {
    const chats = action.payload
    const newChats = []

    if (isArray(chats)) {
      chats.forEach(chat => {
        const chatIndex = state.findIndex(u => u.id === chat.id)
        if (chatIndex !== -1) {
          state[chatIndex] = chat
        } else {
          newChats.push(chat)
        }
      })
    }

    return [...state, ...newChats]
  }

  if (action.type === 'UPDATE_CHATS') {
    const chat = action.payload
    const chatIndex = state.findIndex(u => u.id === chat.id)

    if (chatIndex !== -1) {
      state[chatIndex] = chat
      return [...state]
    } else {
      return [chat, ...state]
    }
  }

  if (action.type === 'DELETE_CHAT') {
    const chatId = action.payload

    const chatIndex = state.findIndex(u => u.id === chatId)
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }

  if (action.type === 'CHANGE_CHAT') {
    const changedChats = state.map(chat => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat
      }
      return chat
    })
    return changedChats
  }
}

const MainListItems = props => {
  const { drawerClose, collapsed } = props
  const classes = useStyles(collapsed)
  const { whatsApps } = useContext(WhatsAppsContext)
  const { user, handleLogout } = useContext(AuthContext)
  const { hasPermission, isAdmin } = usePermission()
  const [connectionWarning, setConnectionWarning] = useState(false)
  const [showCampaigns, setShowCampaigns] = useState(false)
  const [showKanban, setShowKanban] = useState(false)
  const [showCRM, setShowCRM] = useState(false)
  const [showOpenAi, setShowOpenAi] = useState(false)
  const [showIntegrations, setShowIntegrations] = useState(false)
  const location = useLocation()
  const [showSchedules, setShowSchedules] = useState(false)
  const [showInternalChat, setShowInternalChat] = useState(false)
  const [showExternalApi, setShowExternalApi] = useState(false)
  const [activePath, setActivePath] = useState(location.pathname)

  const [invisible, setInvisible] = useState(true)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchParam] = useState('')
  const [chats, dispatch] = useReducer(reducer, [])
  const { getPlanCompany } = usePlans()

  const [version, setVersion] = useState(null)
  const { getVersion } = useVersion()

  const socketManager = useContext(SocketContext)

  const canAccessStandardKanban = showKanban && hasPermission('ver_kanban')
  const canAccessCustomKanban = hasPermission('ver_kanban_personalizado')
  const canAccessCRM = showCRM && hasPermission('ver_crm')
  const canAccessCRMDashboard = showCRM && hasPermission('ver_dashboard_crm')
  const canAccessCRMReports = canAccessCRM && isAdmin
  const canAccessCRMSettings = showCRM && hasPermission('gerenciar_crm_configuracoes')

  const showServiceItems =
    hasPermission('ver_atendimentos') ||
    hasPermission('ver_respostas_rapidas') ||
    canAccessStandardKanban ||
    canAccessCustomKanban ||
    canAccessCRM ||
    canAccessCRMDashboard ||
    canAccessCRMSettings ||
    hasPermission('ver_tarefas') ||
    hasPermission('ver_contatos') ||
    (showSchedules && hasPermission('ver_agendamentos')) ||
    hasPermission('ver_agenda|ver_agenda_propria') ||
    hasPermission('ver_tags') ||
    (showInternalChat && hasPermission('ver_chat_interno')) ||
    hasPermission('ver_ajuda')

  const showAdministration =
    user.super ||
    (showOpenAi && hasPermission('ver_openai')) ||
    (showIntegrations && hasPermission('ver_integracoes')) ||
    hasPermission('ver_conexoes') ||
    hasPermission('ver_lista_arquivos') ||
    hasPermission('ver_filas_chatbot') ||
    hasPermission('manage_roles') ||
    hasPermission('ver_usuarios') ||
    (showExternalApi && hasPermission('ver_api')) ||
    hasPermission('ver_financeiro') ||
    hasPermission('ver_configuracoes') ||
    hasPermission('gerencia_grupos')

  const showCampaignItems = showCampaigns && (hasPermission('ver_listagem') || hasPermission('ver_listas_contatos') || hasPermission('ver_configuracoes_campanhas'))

  useEffect(() => {
    setActivePath(location.pathname)
  }, [location])

  useEffect(() => {
    async function fetchVersion() {
      try {
        const _version = await getVersion()
        if (_version && _version.version) {
          setVersion(_version.version)
        }
      } catch (err) {
        console.error('Erro ao buscar versão:', err)
      }
    }
    fetchVersion()
  }, [getVersion])

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId
      const planConfigs = await getPlanCompany(undefined, companyId)

      setShowCampaigns(planConfigs.plan.useCampaigns !== false)
      setShowKanban(planConfigs.plan.useKanban !== false)
      setShowCRM(planConfigs.plan.useCRM === true)
      setShowOpenAi(planConfigs.plan.useOpenAi !== false)
      setShowIntegrations(planConfigs.plan.useIntegrations !== false)
      setShowSchedules(planConfigs.plan.useSchedules !== false)
      setShowInternalChat(planConfigs.plan.useInternalChat !== false)
      setShowExternalApi(planConfigs.plan.useExternalApi !== false)
    }
    fetchData()
  }, [getPlanCompany, user.companyId])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.getSocket(companyId)

    socket.on(`company-${companyId}-chat`, data => {
      if (data.action === 'new-message') {
        dispatch({ type: 'CHANGE_CHAT', payload: data })
      }
      if (data.action === 'update') {
        dispatch({ type: 'CHANGE_CHAT', payload: data })
      }
    })
    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  useEffect(() => {
    let unreadsCount = 0
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false)
    } else {
      setInvisible(true)
    }
  }, [chats, user.id])

  useEffect(() => {
    if (localStorage.getItem('cshow')) {
      setShowCampaigns(true)
    }
  }, [])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter(whats => {
          return whats.status === 'qrcode' || whats.status === 'PAIRING' || whats.status === 'DISCONNECTED' || whats.status === 'TIMEOUT' || whats.status === 'OPENING'
        })
        if (offlineWhats.length > 0) {
          setConnectionWarning(true)
        } else {
          setConnectionWarning(false)
        }
      }
    }, 2000)
    return () => clearTimeout(delayDebounceFn)
  }, [whatsApps])

  const fetchChats = async () => {
    try {
      const { data } = await api.get('/chats/', {
        params: { searchParam, pageNumber },
      })
      dispatch({ type: 'LOAD_CHATS', payload: data.records })
    } catch (err) {
      toastError(err)
    }
  }

  const handleClickLogout = () => {
    handleLogout()
  }

  const isActivePath = (path, options = {}) => {
    const { exclude = [] } = options

    if (exclude.includes(activePath)) {
      return false
    }

    return activePath === path || activePath.startsWith(path + '/')
  }

  return (
    <div className={classes.menuWrapper} onClick={drawerClose}>
      {showServiceItems && (
        <>
          <ListSubheader hidden={collapsed} className={classes.subheader} inset color='inherit'>
            {i18n.t('Atendimento')}
          </ListSubheader>
          {hasPermission('ver_atendimentos') && (
            <ListItemLink to='/tickets' primary={i18n.t('mainDrawer.listItems.tickets')} icon={<FiMessageSquare size={18} />} isActive={isActivePath('/tickets')} collapsed={collapsed} />
          )}
          {hasPermission('ver_respostas_rapidas') && (
            <ListItemLink to='/quick-messages' primary={i18n.t('mainDrawer.listItems.quickMessages')} icon={<FiZap size={18} />} isActive={isActivePath('/quick-messages')} collapsed={collapsed} />
          )}
          {canAccessStandardKanban && <ListItemLink to='/kanban' primary='Kanban' icon={<FiCheckSquare size={18} />} isActive={isActivePath('/kanban')} collapsed={collapsed} />}
          {canAccessCustomKanban && (
            <ListItemLink to='/custom-kanban' primary={i18n.t('customKanban.menuLabel')} icon={<FiGrid size={18} />} isActive={isActivePath('/custom-kanban')} collapsed={collapsed} />
          )}
          <MenuGroup
            label={i18n.t('crm.menuLabel')}
            icon={<FiBriefcase size={18} />}
            items={getCrmMenuItems({ canAccessCRM, canAccessCRMDashboard, canAccessCRMReports, canAccessCRMSettings })}
            icons={CRM_MENU_ICONS}
            insideGroup={isCrmPath(activePath)}
            isItemActive={isCrmMenuItemActive}
            activePath={activePath}
            collapsed={collapsed}
          />
          {hasPermission('ver_tarefas') && <ListItemLink to='/todolist' primary={i18n.t('Tarefas')} icon={<FiList size={18} />} isActive={isActivePath('/todolist')} collapsed={collapsed} />}
          {hasPermission('ver_contatos') && (
            <ListItemLink to='/contacts' primary={i18n.t('mainDrawer.listItems.contacts')} icon={<FiUser size={18} />} isActive={isActivePath('/contacts')} collapsed={collapsed} />
          )}
          {showSchedules && hasPermission('ver_agendamentos') && (
            <ListItemLink to='/schedules' primary={i18n.t('mainDrawer.listItems.schedules')} icon={<FiCalendar size={18} />} isActive={isActivePath('/schedules')} collapsed={collapsed} />
          )}
          <MenuGroup
            label={i18n.t('mainDrawer.listItems.agenda')}
            icon={<FiClipboard size={18} />}
            items={getAgendaMenuItems({ canAccessAgenda: hasPermission('ver_agenda|ver_agenda_propria'), canManageAgenda: hasPermission('gerenciar_agenda') })}
            icons={AGENDA_MENU_ICONS}
            insideGroup={isAgendaPath(activePath)}
            isItemActive={isAgendaMenuItemActive}
            activePath={activePath}
            collapsed={collapsed}
          />
          {hasPermission('ver_tags') && <ListItemLink to='/tags' primary={i18n.t('mainDrawer.listItems.tags')} icon={<FiTag size={18} />} isActive={isActivePath('/tags')} collapsed={collapsed} />}
          {showInternalChat && hasPermission('ver_chat_interno') && (
            <ListItemLink
              to='/chats'
              primary={i18n.t('mainDrawer.listItems.chats')}
              icon={
                <StyledBadge color='secondary' variant='dot' invisible={invisible}>
                  <FiMessageSquare size={18} />
                </StyledBadge>
              }
              isActive={isActivePath('/chats')}
              collapsed={collapsed}
            />
          )}
          {hasPermission('ver_ajuda') && (
            <ListItemLink to='/helps' primary={i18n.t('mainDrawer.listItems.helps')} icon={<FiHelpCircle size={18} />} isActive={isActivePath('/helps')} collapsed={collapsed} />
          )}
        </>
      )}

      {(hasPermission('ver_dashboard') || hasPermission('ver_relatorios')) && (
        <>
          <ListSubheader hidden={collapsed} className={classes.subheader} inset color='inherit'>
            {i18n.t('Gerência')}
          </ListSubheader>
          {hasPermission('ver_dashboard') && <ListItemLink to='/' primary='Dashboard' icon={<FiHome size={18} />} isActive={isActivePath('/')} collapsed={collapsed} />}
          {hasPermission('ver_relatorios') && (
            <ListItemLink to='/relatorios' primary={i18n.t('Relatórios')} icon={<FiBarChart2 size={18} />} isActive={isActivePath('/relatorios')} collapsed={collapsed} />
          )}
        </>
      )}

      {showCampaignItems && (
        <>
          <ListSubheader hidden={collapsed} className={classes.subheader} inset color='inherit'>
            {i18n.t('Campanhas')}
          </ListSubheader>
          {hasPermission('ver_listagem') && <ListItemLink to='/campaigns' primary={i18n.t('Listagem')} icon={<FiMail size={18} />} isActive={isActivePath('/campaigns')} collapsed={collapsed} />}
          {hasPermission('ver_listas_contatos') && (
            <ListItemLink to='/contact-lists' primary={i18n.t('Listas de Contatos')} icon={<FiUsers size={18} />} isActive={isActivePath('/contact-lists')} collapsed={collapsed} />
          )}
          {hasPermission('ver_configuracoes_campanhas') && (
            <ListItemLink to='/campaigns-config' primary={i18n.t('Configurações')} icon={<FiSettings size={18} />} isActive={isActivePath('/campaigns-config')} collapsed={collapsed} />
          )}
        </>
      )}

      {showAdministration && (
        <>
          <ListSubheader hidden={collapsed} className={classes.subheader} inset color='inherit'>
            {i18n.t('Administração')}
          </ListSubheader>
          {user.super && (
            <ListItemLink to='/announcements' primary={i18n.t('mainDrawer.listItems.annoucements')} icon={<FiBell size={18} />} isActive={isActivePath('/announcements')} collapsed={collapsed} />
          )}
          {showOpenAi && hasPermission('ver_openai') && (
            <ListItemLink to='/prompts' primary={i18n.t('mainDrawer.listItems.prompts')} icon={<FiCpu size={18} />} isActive={isActivePath('/prompts')} collapsed={collapsed} />
          )}
          {showIntegrations && hasPermission('ver_integracoes') && (
            <ListItemLink
              to='/queue-integration'
              primary={i18n.t('mainDrawer.listItems.queueIntegration')}
              icon={<FiLink size={18} />}
              isActive={isActivePath('/queue-integration')}
              collapsed={collapsed}
            />
          )}
          {hasPermission('ver_conexoes') && (
            <ListItemLink
              to='/connections'
              primary={i18n.t('mainDrawer.listItems.connections')}
              icon={
                <Badge badgeContent={connectionWarning ? '!' : 0} color='error' className={classes.connectionBadge}>
                  <FiRefreshCw size={18} />
                </Badge>
              }
              isActive={isActivePath('/connections')}
              collapsed={collapsed}
            />
          )}
          {hasPermission('gerencia_grupos') && (
            <ListItemLink
              to='/group-participants'
              primary={i18n.t('mainDrawer.listItems.groupParticipants')}
              icon={<FiUsers size={18} />}
              isActive={isActivePath('/group-participants')}
              collapsed={collapsed}
            />
          )}
          {hasPermission('ver_lista_arquivos') && (
            <ListItemLink to='/files' primary={i18n.t('mainDrawer.listItems.files')} icon={<FiFolder size={18} />} isActive={isActivePath('/files')} collapsed={collapsed} />
          )}
          {hasPermission('ver_filas_chatbot') && (
            <ListItemLink to='/queues' primary={i18n.t('mainDrawer.listItems.queues')} icon={<FiLayers size={18} />} isActive={isActivePath('/queues')} collapsed={collapsed} />
          )}
          {hasPermission('manage_roles') && <ListItemLink to='/roles' primary='Perfis' icon={<FiKey size={18} />} isActive={isActivePath('/roles')} collapsed={collapsed} />}
          {hasPermission('ver_usuarios') && (
            <ListItemLink to='/users' primary={i18n.t('mainDrawer.listItems.users')} icon={<FiUsers size={18} />} isActive={isActivePath('/users')} collapsed={collapsed} />
          )}
          {showExternalApi && hasPermission('ver_api') && (
            <ListItemLink to='/messages-api' primary={i18n.t('mainDrawer.listItems.messagesAPI')} icon={<FiCode size={18} />} isActive={isActivePath('/messages-api')} collapsed={collapsed} />
          )}
          {hasPermission('ver_financeiro') && (
            <ListItemLink to='/financeiro' primary={i18n.t('mainDrawer.listItems.financeiro')} icon={<FiDollarSign size={18} />} isActive={isActivePath('/financeiro')} collapsed={collapsed} />
          )}
          {hasPermission('ver_configuracoes') && (
            <ListItemLink to='/settings' primary={i18n.t('mainDrawer.listItems.settings')} icon={<FiSettings size={18} />} isActive={isActivePath('/settings')} collapsed={collapsed} />
          )}
          {user.super && (
            <ListSubheader hidden={collapsed} className={classes.subheader} inset color='inherit'>
              {i18n.t('Sistema')}
            </ListSubheader>
          )}
          {user.super && <ListItemLink to='/backups' primary={i18n.t('mainDrawer.listItems.backups')} icon={<FiDatabase size={18} />} isActive={isActivePath('/backups')} collapsed={collapsed} />}
          {!collapsed && version && version !== 'false' && version !== false && (
            <React.Fragment>
              <Divider />
              <div className={classes.versionContainer}>
                {`v${version}`}
                <span className={classes.versionBadge}>latest</span>
              </div>
            </React.Fragment>
          )}
        </>
      )}

      <Divider />
      <li>
        {collapsed ? (
          <CustomTooltip title={i18n.t('Sair')} placement='right' TransitionComponent={Zoom} arrow>
            <ListItem button dense onClick={handleClickLogout} className={classes.logoutButton}>
              <ListItemIcon className={classes.listItemIcon}>
                <div className={classes.iconWrapper}>
                  <FiLogOut size={18} />
                </div>
              </ListItemIcon>
              <ListItemText primary={i18n.t('Sair')} className={classes.listItemText} />
            </ListItem>
          </CustomTooltip>
        ) : (
          <ListItem button dense onClick={handleClickLogout} className={classes.logoutButton}>
            <ListItemIcon className={classes.listItemIcon}>
              <div className={classes.iconWrapper}>
                <FiLogOut size={18} />
              </div>
            </ListItemIcon>
            <ListItemText primary={i18n.t('Sair')} className={classes.listItemText} />
          </ListItem>
        )}
      </li>
    </div>
  )
}

export default MainListItems
